import React, { useEffect, useState } from 'react';
import { Form, Input, Row, Col, Checkbox, DatePicker } from 'antd';
import { useCart } from 'stores/cartPOSContext';
import AutoCompleteCommon from '../AutoComplete/AutoCompleteCommon';
import { ButtonCommon, FancySpinner, FileUploadModal, InputCommon, LabelWithRequired, PrefixedInput } from '..';
import PaymentMethodPanel from './PaymentMethodPanel';
import { formatPrice } from 'utils';
import MemberRankBadge from '../Badge/MemberRankBadge';
import { Info } from '@phosphor-icons/react';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import QuickViewDebtModal from '../Modal/QuickViewDebtModal';
import { phoneNumberRule } from 'utils/formRules';
import { useOrderInfoOperations } from 'hooks/useOrderInfoOperations';
import { useLocation, useSearchParams } from 'react-router-dom';
import Profile from 'models/Profile';
import useAuthContext from 'stores/auth-context';
import CustomerDebtDetailModal from '../Customer/CustomerDebtDetailModal';
import { CustomerCreate } from 'models/Customer';
export const OrderInfoPanel: React.FC = () => {
  const { profile } = useAuthContext();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const [type, setType] = useState(searchParams.get('type') || '');
  const [copyId, setCopyId] = useState(searchParams.get('copyId') || '');

  useEffect(() => {
    const newType = searchParams.get('type') || '';
    const newCopyId = searchParams.get('copyId') || '';
    setType(newType);
    setCopyId(newCopyId);
  }, [location.search]);

  const {
    form,
    // State
    activeCart,
    selectedCustomer,
    setSelectedCustomer,
    customerDebts,
    isOpenDebtModal,
    setIsOpenDebtModal,
    isFileUploadModalOpen,
    setIsFileUploadModalOpen,
    transactionMethod,
    setTransactionMethod,

    // Data
    customers,
    customerOptions,

    // Computed values
    memberDiscount,
    totalAmount,
    memberPoints,
    memberRank,
    memberRankColor,
    hasDebts,
    prefixCode,

    // Event handlers
    handleFinishOrder,
    handleBack,
    handleCustomerInfoChange,
    handleOpenDebtModal,
    handleSelectCustomer,
    generateOrderCode,
    handleUpload,
    handleValidateOrder,
    handleCloseDetailModal,
    handleOpenDebtDetailModal,
    // Loading states
    isGeneratingCode,
    isCreatingSaleOrder,
    isCheckingUnique,
    isLoading,
    isDetailModalOpen,
    idDebt,
  } = useOrderInfoOperations();

  const { updateCustomer, updateCart } = useCart();

  return (
    <div className='p-4 bg-white rounded-lg shadow-sm'>
      {(isCreatingSaleOrder || isCheckingUnique) && (
        <div className='fixed inset-0 flex items-center justify-center bg-white/70 z-50'>
          <FancySpinner size='large' colorScheme='blue' text='Đang cập nhật trạng thái...' overlay={true} />
        </div>
      )}
      <h2 className='text-xl text-[18px] font-bold mb-4'>Thông tin đơn hàng</h2>

      <Form
        layout='vertical'
        form={form}
        initialValues={{
          ...activeCart?.customer,
          code: activeCart?.code,
        }}
        onValuesChange={(changedValues) => {
          if ('code' in changedValues) {
            if (activeCart) {
              updateCart(activeCart.id, { code: changedValues.code });
            }
          } else {
            handleCustomerInfoChange(changedValues);
          }
        }}
        className='form-order-info p-3'
        requiredMark={false}
      >
        <Row gutter={[18, 12]}>
          <Col span={8}>
            <Form.Item
              name='name'
              label={<LabelWithRequired label='Họ và tên' />}
              className='flex-1'
              rules={[{ required: true, message: 'Vui lòng nhập họ tên' }]}
            >
              <AutoCompleteCommon
                sizeSelect='sm'
                placeholderSelect='Chọn hoặc nhập vào họ tên'
                options={customerOptions}
                widthSelect='100%'
                onChange={(value) => {
                  setSelectedCustomer(null);
                  updateCustomer({
                    ...activeCart?.customer,
                    name: value as string,
                    id: undefined,
                  });
                }}
                onSelect={handleSelectCustomer}
                loading={isLoading}
              />
            </Form.Item>
          </Col>
          <Col span={16} className='flex items-end pb-1'>
            <div className='flex items-center text-sm 2xl:gap-3 gap-2 font-medium'>
              {selectedCustomer && (
                <>
                  <span className='text-[var(--textBlack56)]'>Điểm tích lũy </span>
                  <span>{formatPrice(memberPoints)} VNĐ</span>
                  {memberPoints > 0 && memberRank && <MemberRankBadge name={memberRank} color={memberRankColor} />}
                  {hasDebts && (
                    <>
                      <span className='text-[var(--sematicErrorColor500)] flex items-center gap-1'>
                        <Info size={20} /> Có công nợ
                      </span>
                      <span
                        onClick={handleOpenDebtModal}
                        className='text-[var(--primaryColor500)] font-bold cursor-pointer'
                      >
                        Xem nhanh
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
          </Col>
          <Col span={8}>
            <Form.Item
              name='code'
              label={<LabelWithRequired label='Mã đơn hàng' />}
              className='flex-1'
              rules={[{ required: true, message: 'Vui lòng nhập mã đơn hàng' }]}
            >
              {/* <InputCommon
                hardSize='md2'
                disabled={isGeneratingCode}
                suffix={
                  <ButtonCommon
                    color='none'
                    size='sm'
                    onClick={generateOrderCode}
                    loading={isGeneratingCode}
                    className='text-[var(--primaryColor500)] font-medium'
                  >
                    Generate
                  </ButtonCommon>
                }
              /> */}
              <PrefixedInput
                prefix={prefixCode || ''}
                placeholder='Nhập vào mã...'
                disabled={isGeneratingCode}
                inputProps={{
                  suffix: (
                    <ButtonCommon color='barcode' fullWidth radius='rounded' size='xs' onClick={generateOrderCode}>
                      Generate
                    </ButtonCommon>
                  ),
                  // className: 'xl:!h-[36px] !text-[14px] ',
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name='phone'
              label={<LabelWithRequired label='Số điện thoại' />}
              className='flex-1'
              rules={[{ required: true, message: 'Vui lòng nhập số điện thoại' }, phoneNumberRule()]}
            >
              <AutoCompleteCommon
                sizeSelect='sm'
                placeholderSelect='Nhập số điện thoại'
                options={customers.map((customer) => ({
                  label: customer.baseUser?.phoneNumber || '',
                  value: customer.id,
                }))}
                widthSelect='100%'
                onChange={(value) => {
                  setSelectedCustomer(null);
                  updateCustomer({
                    ...activeCart?.customer,
                    id: undefined,
                    phone: value as string,
                  });
                }}
                onSelect={handleSelectCustomer}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name='email' label={'Email'} rules={[{ type: 'email', message: 'Vui lòng nhập email hợp lệ' }]}>
              <AutoCompleteCommon
                sizeSelect='sm'
                placeholderSelect='Nhập email'
                options={customers
                  .filter((c) => c.baseUser?.email && c.baseUser?.email !== '')
                  .map((customer) => ({
                    label: customer.baseUser?.email || '',
                    value: customer.id,
                  }))}
                widthSelect='100%'
                onChange={(value) => {
                  setSelectedCustomer(null);
                  updateCustomer({
                    ...activeCart?.customer,
                    id: undefined,
                    email: value as string,
                  });
                }}
                onSelect={handleSelectCustomer}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name='address' label='Địa chỉ'>
              <InputCommon placeholder='Nhập địa chỉ' />
            </Form.Item>
          </Col>
          <Col span={24}>
            <div className=''>
              <div className='mb-2 w-[281px]'>Thời gian lên đơn </div>
              <Form.Item

                name='createdAt'
                className='flex-1 mb-0 w-[281px]'
                getValueProps={(value) => ({
                  value: value ? dayjs(value) : null,
                })}
              >
                <DatePicker
                  placeholder={dayjs().format('DD/MM/YYYY')}
                  format='DD/MM/YYYY'
                  className='w-full h-[38px] w-[281px3]'
                  suffixIcon={<CalendarOutlined />}
                  disabledDate={(current) => current && current > dayjs().endOf('day')}
                />
              </Form.Item>
            </div>
          </Col>



          <Col span={24}>
            <Checkbox checked className='mr-2 checkbox-disabled cursor-not-allowed' />
            Lưu thông tin khách hàng
          </Col>
          <Col span={24}>
            <Form.Item name='note' label='Ghi chú'>
              <Input.TextArea className='p-2' placeholder='Nhập ghi chú đơn hàng' rows={2} />
            </Form.Item>
            <Form.Item name='status' hidden>
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <PaymentMethodPanel
          memberDiscount={memberDiscount}
          form={form}
          totalAmount={totalAmount}
          onFinish={() => {
            handleValidateOrder().then((isValid) => {
              if (!isValid) {
                return;
              } else {
                console.log('Validation passed, finishing order...');
                setIsFileUploadModalOpen(true);
              }
            });
          }}
          onBack={handleBack}
          setTransactionMethod={setTransactionMethod}
          transactionMethod={transactionMethod}
        />
      </Form>
      <QuickViewDebtModal
        debts={customerDebts}
        isOpen={isOpenDebtModal}
        onClick={handleOpenDebtDetailModal}
        onClose={() => setIsOpenDebtModal(false)}
      />
      <CustomerDebtDetailModal
        profile={profile as Profile}
        key={'15'}
        debtid={idDebt as string}
        customer={customerDebts as CustomerCreate}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
      />
      <FileUploadModal
        isOpen={isFileUploadModalOpen}
        onClose={() => {
          setIsFileUploadModalOpen(false);
        }}
        title={'TẢI LÊN CHỨNG TỪ THANH TOÁN'}
        subtitle={'Vui lòng tải lên thông tin hình ảnh và xác nhận với kế toán'}
        acceptedFileTypes={'.xlsx,.xls,.xlsm,.xlm,.png,.jpg,.jpeg,.webp,.gif,.bmp'}
        fileTypesHelpText={'Hỗ trợ các tệp tin excel, png, jpg, gif, bmp, ...'}
        multiple={false}
        positionButton='center'
        confirmText='Xác nhận đã thanh toán'
        typeModal='upload'
        maxFileSize={5 * 1024 * 1024}
        onUpload={handleUpload}
        disabled={false}
        onComplete={async (files) => {
          // if (files.length > 0) {
          //   try {
          //     await handleFinishOrder();
          //     setIsFileUploadModalOpen(false);
          //   } catch (error) {
          //     console.error('Error confirming payment:', error);
          //   }
          // } else {
          //   console.warn('No files uploaded');
          // }
          console.log(files);
          try {
            await handleFinishOrder(copyId || undefined, type || undefined);
            setIsFileUploadModalOpen(false);
          } catch (error) {
            console.error('Error confirming payment:', error);
          }
        }}
      />
    </div>
  );
};
